import { Alert, Checkbox, FormField, Input, RichTextEditor, TextArea } from '@sel/ui';
import type { Override } from '@sel/utils';
import { noop } from '@tanstack/react-query';
import {
  useController,
  type Control,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
  type GlobalError,
  type UseFormReturn,
} from 'react-hook-form';

import { RichTextToolbar } from './rich-text-toolbar';

type ControlledProps = 'value' | 'defaultValue' | 'onChange' | 'onBlur' | 'ref';

type FieldProps<Values extends FieldValues, Transformed> = {
  control: Control<Values, unknown, Transformed>;
  name: FieldPath<Values>;
  label: React.ReactNode;
  hint?: React.ReactNode;
};

export function submitWithMutation<Values extends FieldValues, Transformed>(
  form: UseFormReturn<Values, unknown, Transformed>,
  mutation: { mutateAsync: (variables: Transformed) => Promise<unknown> },
  { onInvalid }: { onInvalid?: (errors: FieldErrors<Values>) => void } = {},
): React.EventHandler<React.SyntheticEvent> {
  // The mutation's onError handles the failure; handleSubmit would rethrow it as an unhandled rejection.
  const submit = form.handleSubmit((body) => mutation.mutateAsync(body).catch(noop), onInvalid);

  return (event) => {
    void submit(event);
  };
}

type InputFieldProps<Values extends FieldValues, Transformed> = Override<
  Omit<React.ComponentProps<typeof Input>, Exclude<ControlledProps, 'onChange'>>,
  FieldProps<Values, Transformed>
>;

export function InputField<Values extends FieldValues, Transformed>({
  control,
  name,
  label,
  hint,
  onChange,
  ...props
}: InputFieldProps<Values, Transformed>) {
  const { field, fieldState } = useController({ control, name });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    field.onChange(event);
    onChange?.(event);
  };

  return (
    <FormField label={label} hint={hint} error={fieldState.error?.message}>
      <Input {...props} {...field} onChange={handleChange} />
    </FormField>
  );
}

type TextAreaFieldProps<Values extends FieldValues, Transformed> = Override<
  Omit<React.ComponentProps<typeof TextArea>, ControlledProps>,
  FieldProps<Values, Transformed>
>;

export function TextAreaField<Values extends FieldValues, Transformed>({
  control,
  name,
  label,
  hint,
  ...props
}: TextAreaFieldProps<Values, Transformed>) {
  const { field, fieldState } = useController({ control, name });

  return (
    <FormField label={label} hint={hint} error={fieldState.error?.message}>
      <TextArea {...props} {...field} />
    </FormField>
  );
}

type CheckboxFieldProps<Values extends FieldValues, Transformed> = Override<
  Omit<
    React.ComponentProps<typeof Checkbox>,
    Exclude<ControlledProps, 'onChange'> | 'checked' | 'defaultChecked'
  >,
  Omit<FieldProps<Values, Transformed>, 'hint'>
>;

export function CheckboxField<Values extends FieldValues, Transformed>({
  control,
  name,
  onChange,
  ...props
}: CheckboxFieldProps<Values, Transformed>) {
  const {
    field: { value, ...field },
  } = useController({ control, name });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    field.onChange(event.target.checked);
    onChange?.(event);
  };

  return <Checkbox {...props} {...field} checked={Boolean(value)} onChange={handleChange} />;
}

type RichTextFieldProps<Values extends FieldValues, Transformed> = Override<
  Omit<React.ComponentProps<typeof RichTextEditor.Root>, ControlledProps | 'children'>,
  FieldProps<Values, Transformed>
>;

export function RichTextField<Values extends FieldValues, Transformed>({
  control,
  name,
  label,
  hint,
  ...props
}: RichTextFieldProps<Values, Transformed>) {
  const { field, fieldState } = useController({ control, name });

  return (
    <FormField label={label} hint={hint} error={fieldState.error?.message}>
      <RichTextEditor.Root {...props} {...field}>
        <RichTextEditor.Textarea
          toolbar={
            <RichTextEditor.Toolbar>
              <RichTextToolbar.Bold />
              <RichTextToolbar.Italic />
              <RichTextToolbar.Underline />
              <RichTextToolbar.Link />
              <RichTextToolbar.BulletList />
              <RichTextToolbar.OrderedList />
            </RichTextEditor.Toolbar>
          }
        />
      </RichTextEditor.Root>
    </FormField>
  );
}

type FormServerErrorAlertProps = {
  error?: GlobalError;
  title: React.ReactNode;
};

export function FormServerErrorAlert({ error, title }: FormServerErrorAlertProps) {
  if (error?.type !== 'server') {
    return null;
  }

  return (
    <Alert.Root tone="danger">
      <Alert.Title>{title}</Alert.Title>
      <Alert.Description>{error.message}</Alert.Description>
    </Alert.Root>
  );
}
